// Simple consent management utilities without React context

export interface ConsentState {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

const CONSENT_COOKIE_NAME = 'citysage-cookie-consent'
const CONSENT_VERSION = '1.0'

/** Footer "Cookie settings" asks the banner to open again. */
export const OPEN_CONSENT_EVENT = 'citysage:open-consent'

export function requestConsentPreferences(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT))
}

// Consent duration options (in milliseconds)
export const CONSENT_DURATIONS = {
  ONE_MONTH: 30 * 24 * 60 * 60 * 1000,
  THREE_MONTHS: 90 * 24 * 60 * 60 * 1000,
  SIX_MONTHS: 180 * 24 * 60 * 60 * 1000,
  ONE_YEAR: 365 * 24 * 60 * 60 * 1000,          // Industry standard
  THIRTEEN_MONTHS: 395 * 24 * 60 * 60 * 1000,   // Common practice (13 months)
} as const

// Default consent duration - 13 months is industry standard for cookie consent
// This balances user experience with GDPR compliance requirements
const DEFAULT_CONSENT_DURATION = CONSENT_DURATIONS.THIRTEEN_MONTHS

interface ConsentData {
  consent: ConsentState
  timestamp: number
  version: string
}

const defaultConsentState: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
}

/**
 * Legacy cookie name from old react-cookie-consent implementation
 */
const LEGACY_COOKIE_NAME = 'CitySageCookieConsent'

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

/**
 * Delete a cookie by name
 */
function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax`
}

/**
 * Clean up legacy cookie consent - remove old cookie and re-request consent
 * This ensures users see the new consent banner with granular options
 */
function migrateLegacyConsent(): void {
  // Check if we've already cleaned up (don't run twice)
  if (localStorage.getItem(`${CONSENT_COOKIE_NAME}_legacy_cleaned`)) {
    return
  }

  // Check for legacy cookie
  const legacyCookie = getCookie(LEGACY_COOKIE_NAME)
  if (legacyCookie) {
    // Remove old cookie - user will need to consent again with new granular system
    deleteCookie(LEGACY_COOKIE_NAME)
    
    // Mark as cleaned up
    localStorage.setItem(`${CONSENT_COOKIE_NAME}_legacy_cleaned`, 'true')
    
    console.log('[Consent] Removed legacy cookie - user will be asked to consent again with new system')
  }
}

/**
 * Initialize consent system and clean up legacy cookies
 * Removes old cookie consent so users can consent again with the new granular system
 * Call this once on app startup
 */
export function initializeConsent(): void {
  migrateLegacyConsent()
}

/**
 * Get current consent state from localStorage
 * @param customDuration - Optional custom duration in milliseconds (defaults to 13 months)
 */
export function getConsent(customDuration?: number): ConsentState | null {
  try {
    const savedConsent = localStorage.getItem(CONSENT_COOKIE_NAME)
    if (!savedConsent) return null

    const parsed: ConsentData = JSON.parse(savedConsent)
    
    // Check if consent is still valid (default: 13 months)
    const consentDuration = customDuration ?? DEFAULT_CONSENT_DURATION
    const isValid = Date.now() - parsed.timestamp < consentDuration
    const isCurrentVersion = parsed.version === CONSENT_VERSION
    
    if (isValid && isCurrentVersion) {
      return parsed.consent
    }
    
    // If expired or wrong version, clean up old consent
    localStorage.removeItem(CONSENT_COOKIE_NAME)
    return null
  } catch (error) {
    console.warn('Invalid consent data in localStorage:', error)
    // Clean up corrupted data
    localStorage.removeItem(CONSENT_COOKIE_NAME)
    return null
  }
}

/**
 * Check if user has given consent (any consent, not just all)
 * @param customDuration - Optional custom duration in milliseconds (defaults to 13 months)
 */
export function hasConsented(customDuration?: number): boolean {
  return getConsent(customDuration) !== null
}

/**
 * Save consent state to localStorage
 */
export function saveConsent(consent: ConsentState): void {
  const consentData: ConsentData = {
    consent: { ...consent, necessary: true }, // Always ensure necessary is true
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  }
  
  localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(consentData))
  
  // Dispatch event for any components that need to listen
  window.dispatchEvent(new CustomEvent('consentUpdated', { 
    detail: consent 
  }))
}

/**
 * Accept all cookies
 */
export function acceptAllConsent(): ConsentState {
  const allConsent: ConsentState = {
    necessary: true,
    analytics: true,
    marketing: true,
  }
  saveConsent(allConsent)
  return allConsent
}

/**
 * Accept only necessary cookies
 */
export function acceptNecessaryOnly(): ConsentState {
  const minimalConsent: ConsentState = {
    necessary: true,
    analytics: false,
    marketing: false,
  }
  saveConsent(minimalConsent)
  return minimalConsent
}

/**
 * Reset consent (clear from localStorage)
 */
export function resetConsent(): void {
  localStorage.removeItem(CONSENT_COOKIE_NAME)
  window.dispatchEvent(new CustomEvent('consentUpdated', { 
    detail: null 
  }))
}

/**
 * Check if user should see the banner
 * @param customDuration - Optional custom duration in milliseconds (defaults to 13 months)
 */
export function shouldShowBanner(customDuration?: number): boolean {
  return !hasConsented(customDuration)
}

/**
 * Get the expiration date for current consent
 */
export function getConsentExpirationDate(): Date | null {
  try {
    const savedConsent = localStorage.getItem(CONSENT_COOKIE_NAME)
    if (!savedConsent) return null

    const parsed: ConsentData = JSON.parse(savedConsent)
    return new Date(parsed.timestamp + DEFAULT_CONSENT_DURATION)
  } catch {
    return null
  }
}

/**
 * Get days remaining until consent expires
 */
export function getConsentDaysRemaining(): number | null {
  const expirationDate = getConsentExpirationDate()
  if (!expirationDate) return null
  
  const now = new Date()
  const diffTime = expirationDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  return Math.max(0, diffDays)
}

/**
 * GDPR Compliance Notes:
 * 
 * - 13 months is industry standard for cookie consent duration
 * - GDPR doesn't specify exact duration, but requires consent to be "informed" and "specific"
 * - Users can withdraw consent at any time via browser settings or by clearing localStorage
 * - Consent is automatically invalidated when version changes (for new privacy policies)
 * - This implementation follows best practices from major websites and GDPR guidance
 */

